"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import AuthLayout from "./templates/AuthLayout";
import EmailStep from "./molecules/EmailStep";
import PasswordStep from "./molecules/PasswordStep";
import LoadingStep from "./molecules/LoadingStep";
import {
  getAccessToken,
  loginWithCredentials,
  renewSession,
  saveSession,
} from "@/lib/auth/client";
import type { LoginStep } from "./types";
import { validateEmailExists } from "@/lib/auth/validateEmailExists";

/**
 * @component Login
 * @description Client-side login component that manages a three-step flow:
 * email entry, password entry, and a loading state. Checks whether the
 * email exists in Supabase Auth before advancing to the password step.
 * @returns The complete login flow with step-based navigation.
 */
export default function Login() {
  const router = useRouter();
  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      await renewSession();
      if (getAccessToken()) {
        router.replace("/");
      }
    };

    void checkSession();
  }, [router]);

  const handleEmailSubmit = async (submittedEmail: string) => {
    setEmail(submittedEmail);
    setLoading(true);
    setError(null);

    try {
      const exists = await validateEmailExists(submittedEmail);
      if (!exists) {
        setError("No account found for this email.");
        setLoading(false);
        return;
      }
      setStep("password");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * @method handlePasswordSubmit
   * @description Handles password submission, authenticates user, and persists session based on remember-me.
   * @param password - Password entered by the user.
   * @param rememberMe - Whether session should persist across browser restarts.
   * @returns A promise that resolves when login flow completes.
   */
  const handlePasswordSubmit = async (
    password: string,
    rememberMe: boolean,
  ) => {
    setError(null);
    setLoading(true);
    setStep("loading");

    try {
      const session = await loginWithCredentials(email, password);
      saveSession(session, rememberMe);
      router.push("/");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed";
      setError(message);
      setLoading(false);
      setStep("password");
    }
  };

  return (
    <AuthLayout>
      {step === "email" && (
        <EmailStep
          onContinue={handleEmailSubmit}
          loading={loading}
          error={error}
        />
      )}

      {step === "password" && (
        <PasswordStep
          email={email}
          onBack={() => {
            setError(null);
            setStep("email");
          }}
          onContinue={handlePasswordSubmit}
          error={error}
          loading={loading}
        />
      )}

      {step === "loading" && <LoadingStep />}
    </AuthLayout>
  );
}
